import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { BehaviorSubject, Observable, Subject, finalize, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FileModel } from '../../models/file-model';
import { Helpers } from '../../utils/helpers-util';
import { ToasterService } from 'src/app/utils/toaster.service';
import { AccountsPayableService } from 'src/app/services/accounts-payable.service';

@Component({
  selector: 'attachments-list',
  templateUrl: './attachments-list.component.html'
})
export class AttachmentsListComponent {

  @Input()
  public files: FileModel[];

  @Output()
  public onClosed: EventEmitter<void> = new EventEmitter();

  public isHttpRequestInProgress: boolean = false;

  public FileHelper = Helpers;

  isLoading$ = new BehaviorSubject<boolean>(false);

  private readonly downloadStaggerMs = 600;

  private ngUnSubscribe: Subject<void> = new Subject<void>();

  constructor(
    private cd: ChangeDetectorRef,
    private toasterService: ToasterService,
    private accountPayableSvc: AccountsPayableService
  ) {

  }

  ngOnInit() {

  }

  getFileIcon(fileType: string): string {
    return fileType === 'pdf' ? 'assets/pdf-icon.png' : 'assets/doc-icon.png';
  }

  viewFile(file: FileModel): void {
    if (!file) {
      this.toasterService.showError('Invalid file details.');
      return;
    }

    this.isLoading$.next(true);
    this.markForCheck();

    if (file.stream) {
      this.accountPayableSvc.viewFile(file, file.stream, this.toasterService);
      setTimeout(() => {
        this.isLoading$.next(false);
        this.markForCheck();
      }, 300);
      return;
    }

    if (!file.webUrl) {
      this.toasterService.showError('File URL is missing.');
      this.isLoading$.next(false);
      this.markForCheck();
      return;
    }

    this.accountPayableSvc.getFileStream({ webUrl: file.webUrl })
      .pipe(
        finalize(() => {
          setTimeout(() => {
            this.isLoading$.next(false);
            this.markForCheck();
          }, 300);
        })
      )
      .subscribe({
        next: (response: { stream: string }) => {
          if (!response?.stream) {
            this.toasterService.showError('File stream not found.');
            return;
          }

          this.accountPayableSvc.viewFile(file, response.stream, this.toasterService);
        },
        error: (error) => {
          console.error('Error fetching file stream:', error);
          this.toasterService.showError('Failed to load file for preview.');
        }
      });
  }

  downloadFile(file: FileModel): void {
    if (!file) {
      this.toasterService.showError('Invalid file details.');
      return;
    }

    this.isLoading$.next(true);
    this.resolveFileStream(file)
      .pipe(
        finalize(() => {
          this.isLoading$.next(false);
          this.markForCheck();
        })
      )
      .subscribe({
        next: (stream) => this.triggerDownload(file, stream),
        error: () => this.toasterService.showError('Failed to download file.'),
      });
  }

  downloadAllAttachments(): void {
    const files = (this.files || []).filter((file) => file?.webUrl || file?.stream);

    if (!files.length) {
      this.toasterService.showError('No attachments available to download.');
      return;
    }

    if (files.length === 1) {
      this.downloadFile(files[0]);
      return;
    }

    this.isLoading$.next(true);

    forkJoin(
      files.map((file) =>
        this.resolveFileStream(file).pipe(
          map((stream) => ({ file, stream })),
          catchError(() => of({ file, stream: null as string | null }))
        )
      )
    )
      .pipe(
        finalize(() => {
          this.isLoading$.next(false);
          this.markForCheck();
        })
      )
      .subscribe({
        next: (results) => {
          results.forEach(({ file, stream }, index) => {
            setTimeout(() => this.triggerDownload(file, stream), index * this.downloadStaggerMs);
          });
        },
        error: () => this.toasterService.showError('Failed to download attachments.'),
      });
  }

  public close(): void {
    this.onClosed.emit();
  }

  private resolveFileStream(file: FileModel): Observable<string | null> {
    if (file.stream) {
      return of(file.stream);
    }

    if (!file.webUrl) {
      return of(null);
    }

    return this.accountPayableSvc.getFileStream({ webUrl: file.webUrl }).pipe(
      map((response: { stream?: string }) => response?.stream ?? null)
    );
  }

  private triggerDownload(file: FileModel, stream: string | null): void {
    if (stream) {
      const blob = Helpers.base64ToBlob(stream, file.contentType || 'application/octet-stream');
      Helpers.downloadBlob(blob, file.name);
      return;
    }

    if (file.webUrl) {
      Helpers.downloadFile(file.webUrl, file.name);
      return;
    }

    this.toasterService.showError(`Unable to download ${file.name || 'file'}.`);
  }

  private markForCheck(): void {
    this.cd.markForCheck();
  }

  ngOnDestroy() {
    this.ngUnSubscribe.next();
    this.ngUnSubscribe.complete();
  }

}
